import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import Card from "../card";
import InputField from "../form-fields/InputField";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './tenant.css';
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import { UserLoginData } from "../../components/common/charts/Enums";
import { TenantService, TenantVM } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { URLS } from "../../constants/constants";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import SimpleSpinner from "../../components/loader";
import PhoneInput from "../form-fields/PhoneInput";
import EmailInput from "../form-fields/EmailInput";
import Checkbox from "../form-fields/checkbox";
import { TextCss } from "../../components/GlobalCss/TextCss";
import { getWhatsAppEnabled } from "../../services/whatsApp.service";
import { useError } from "../../utils/context/ErrorContext";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";

type FormValues = {
  Id: Number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  unitId: string;
  whatsAppOptIn: boolean;
};

interface EditTenantProps {
  fetchData?: any;
}

/** Strip non-digits; drop leading US/CA "1" so API vs react-phone-input-2 forms compare equal. */
export const normalizePhoneForCompare = (value?: string | null) => {
  const digits = (value ?? "").replace(/\D/g, "");
  return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
};

const EditTenant: React.FC<EditTenantProps> = ({ fetchData }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const userId = Number(sessionStorage.getItem(UserLoginData.user_id));
  const [currentTenantId, setCurrentTenantId] = useState(0);
  const [whatsAppEnabled, setWhatsAppEnabled] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const { clearErrors: clearContextErrors } = useError();
  const loadedFormValuesRef = useRef<FormValues | null>(null);
  const originalPhoneRef = useRef<string>("");

  const methods = useForm<FormValues>({
    defaultValues: {
      Id: 0,
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      unitId: "",
      whatsAppOptIn: false,
    },
    mode: "onBlur",
  });

  const { handleSubmit, control, watch, formState: { isDirty, isValid } } = methods;
  const whatsAppOptIn = watch("whatsAppOptIn");

  // RHF isDirty stays true after a field is reverted to its loaded value (SS10 gotcha 9).
  // Snapshot + phone normalize mirror EditContratorForm so Save disables on true revert.
  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<
    Omit<FormValues, "phoneNumber" | "Id">
  >(control, ["firstName", "lastName", "email", "unitId", "whatsAppOptIn"]);

  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const phoneNumberChanged =
    normalizePhoneForCompare(watchedPhoneNumber) !==
    normalizePhoneForCompare(originalPhoneRef.current);

  const hasUnsavedChanges = isDirty && (hasFieldChanges || phoneNumberChanged);

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [tenantRes, waEnabled] = await Promise.all([
        apiHelper(
          TenantService.getApiTenant1,
          { showNotification: false },
          id
        ),
        getWhatsAppEnabled(),
      ]);

      setWhatsAppEnabled(waEnabled);
      const tenantData = tenantRes?.data?.success as TenantVM | undefined;

      if (tenantData) {
        setCurrentTenantId(tenantData.id);

        const loadedValues: FormValues = {
          Id: tenantData.id,
          firstName: tenantData.firstName,
          lastName: tenantData.lastName,
          email: tenantData.email,
          phoneNumber: tenantData.phoneNumber,
          unitId: tenantData.unitId?.toString() ?? "",
          whatsAppOptIn: Boolean((tenantData as any)?.whatsAppOptIn),
        };
        loadedFormValuesRef.current = loadedValues;
        originalPhoneRef.current = loadedValues.phoneNumber ?? "";
        setSnapshot({
          firstName: loadedValues.firstName,
          lastName: loadedValues.lastName,
          email: loadedValues.email,
          unitId: loadedValues.unitId,
          whatsAppOptIn: loadedValues.whatsAppOptIn,
        });
        methods.reset(loadedValues);
      }
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load tenant data",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, methods, setSnapshot]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const onSubmit = useCallback(
    async (formData: FormValues) => {
      setIsLoading(true);
      try {
        const tenantResponse = await apiHelper(
          TenantService.putApiTenant,
          {
            successMessage: "Tenant Updated Successfully",
            showNotification: true,
          },
          {
            id: currentTenantId,
            userId: userId,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: formData.phoneNumber,
            whatsAppOptIn: formData.whatsAppOptIn,
            email: formData.email,
            unitId: formData.unitId,
          }
        );

        if (tenantResponse?.data?.success) {
          methods.reset();
          fetchData?.();
          const referrer = location.state?.from || URLS.TENANTS;
          navigate(referrer, { state: { activeTab: location.state?.activeTab } });
        } else if (tenantResponse?.errors?.Email) {
          PubSub.publish(TopicsEnum.Notification.toString(), {
            message: `${tenantResponse.errors?.Email[0]}`,
            type: NotificationType.Failure,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [userId, navigate, fetchData, currentTenantId, methods, location]
  );

  const discardAndNavigateBack = useCallback(() => {
    clearContextErrors();
    if (loadedFormValuesRef.current) {
      methods.reset(loadedFormValuesRef.current);
    }
    navigate(-1);
  }, [clearContextErrors, methods, navigate]);

  const onCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsUnsavedChangesOpen(true);
      return;
    }
    discardAndNavigateBack();
  }, [hasUnsavedChanges, discardAndNavigateBack]);

  const handleCancelAction = useCallback(() => {
    setIsUnsavedChangesOpen(false);
  }, []);

  const handleContinueWithoutSaving = useCallback(() => {
    setIsUnsavedChangesOpen(false);
    discardAndNavigateBack();
  }, [discardAndNavigateBack]);

  return (
    <>
      <div className="d-flex flex-column gap-3 full-page-content edit-tenant-container">
        <LeftArrowTopHeader
          title="Edit Tenant"
          backPath={URLS.TENANTS}
          onCancel={onCancel}
        />
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
            <SimpleSpinner myClassName="Icon40 mt-5" />
          </div>
        ) : (
          <FormProvider {...methods}>
            <Card>
              <div className="row">
                <div className="col-12">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-12 col-md-6 col-lg-4">
                        <InputField
                          name="firstName"
                          label="First Name"
                          required
                          placeholder="Enter First Name"
                          requiredMessage="First name is required"
                          onlyAlphabets={true}
                          validation={{
                            minLength: { value: 2, message: "First name must be at least 2 characters long" },
                            maxLength: { value: 20, message: "First name cannot exceed 20 characters" }
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-lg-4">
                        <InputField
                          name="lastName"
                          label="Last Name"
                          required
                          placeholder="Enter Last Name"
                          requiredMessage="Last name is required"
                          onlyAlphabets={true}
                          validation={{
                            minLength: { value: 2, message: "Last name must be at least 2 characters long" },
                            maxLength: { value: 20, message: "Last name cannot exceed 20 characters" }
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4">
                        <EmailInput
                          name="email"
                          label="Email Address"
                          required
                          requiredMessage="Email is required"
                          placeholder="Enter Email Address"
                          validation={{
                            maxLength: { value: 50, message: "Email cannot exceed 50 characters" }
                          }}
                        />
                      </div>

                      <div className="col-12 col-md-6 col-lg-4">
                        <PhoneInput
                          key={whatsAppEnabled ? "phone-wa-all" : "phone-us-only"}
                          name="phoneNumber"
                          label="Contact No"
                          required
                          requiredMessage="Phone number is required"
                          placeholder="Enter Contact No"
                          defaultCountry="us"
                          onlyCountries={whatsAppEnabled ? [] : ["us"]}
                        />
                      </div>

                      {whatsAppEnabled && (
                        <div className="col-12 mt-2">
                          <Controller
                            name="whatsAppOptIn"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                isChecked={field.value}
                                onChange={field.onChange}
                                label="Enable WhatsApp messages for this tenant"
                              />
                            )}
                          />
                          {whatsAppOptIn && (
                            <p className={`${TextCss.sub_heading_400_14} mt-1 mb-0`} style={{ color: "#64748b" }}>
                              Landlord chat messages will also be sent to this WhatsApp number.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            </Card>

            <FixedFooter
              onCancel={onCancel}
              onSubmit={handleSubmit(onSubmit)}
              loading={isLoading}
              buttonLabel={"Save"}
              isSubmitDisabled={isLoading || !hasUnsavedChanges || !isValid}
            />
          </FormProvider>
        )}
      </div>
      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </>
  );
};

export default EditTenant;
